/**
 * Wrapper around generated RTK Query API with cleaner naming and custom logic.
 *
 * All endpoint references (keys, hooks, endpoint map) are derived from the
 * central definitions in `./config.ts` so generated names live in one place.
 */
import { generatedApi } from './api.generated';
import {
  createEndpointExports,
  createHookExports,
  provideListTags,
  LIST_TAG_ID,
} from '@skynet/data-helpers';
import type { HookExports } from '@skynet/data-helpers';
import { BOT_COMMANDS_API_ENDPOINTS as cfg } from './config';

/**
 * Cache tags follow the recommended `id: 'LIST'` recipe:
 * https://redux-toolkit.js.org/rtk-query/usage/automated-refetching#recipes
 *
 * A single `BotCommands` tag type is used for both the collection and individual
 * entities. The list query provides a per-item tag for every command plus a
 * dedicated `{ type: 'BotCommands', id: 'LIST' }` tag, so that:
 * - updating/deleting a single command invalidates only that command's `{ id }`
 *   tag, which refetches both the affected `getBotCommand` query and the list
 *   (the list provides that same per-item tag);
 * - creating a command invalidates `{ id: 'LIST' }` to refetch the collection
 *   without touching unrelated entities.
 *
 * This replaces the previous manual `dispatch(invalidateTags(...))` workaround in
 * `onQueryStarted`: that was attributed to redux-toolkit#5093, but that issue is
 * about the `isSuccess` flag during pessimistic updates, not tag invalidation —
 * `invalidatesTags` propagates to the list correctly via the per-item tags.
 */
export const botCommandsApi = generatedApi.enhanceEndpoints({
  addTagTypes: ['BotCommands'],
  endpoints: {
    [cfg.createBotCommand.generated]: {
      invalidatesTags: [{ type: 'BotCommands', id: LIST_TAG_ID }],
    },
    [cfg.listBotCommands.generated]: {
      providesTags: (result) => provideListTags(result, 'BotCommands'),
    },
    [cfg.getBotCommand.generated]: {
      providesTags: (_result, _error, arg) => [{ type: 'BotCommands', id: arg.id }],
    },
    [cfg.updateBotCommand.generated]: {
      invalidatesTags: (_result, _error, arg) => [{ type: 'BotCommands', id: arg.id }],
    },
    [cfg.deleteBotCommand.generated]: {
      invalidatesTags: (_result, _error, arg) => [{ type: 'BotCommands', id: arg.id }],
    },
    [cfg.restoreBotCommand.generated]: {
      // Restoring a soft-deleted command brings it back into the collection and
      // changes that entity, so invalidate both its per-item tag and the list.
      invalidatesTags: (_result, _error, arg) => [
        { type: 'BotCommands', id: arg.id },
        { type: 'BotCommands', id: LIST_TAG_ID },
      ],
    },
  },
});

// Re-export hooks with cleaner names via endpoint definitions
export const hookExports: HookExports<typeof cfg, typeof botCommandsApi> = createHookExports(
  cfg,
  botCommandsApi,
);

// Re-export endpoints object with cleaner names for direct usage
export const botCommandApiEndpoints = createEndpointExports(cfg, botCommandsApi);
